import {
  Button,
  Combobox,
  DataTable,
  type DataTableColumn,
  Modal,
  Select,
} from "@etudescarre/ui";
import { isNil } from "@etudescarre/utils";
import { MagnifyingGlassIcon, XIcon } from "@phosphor-icons/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { apiClient } from "@/lib/api/client";

type HistoryItem = {
  id: string;
  numericId: number;
  yardId: number;
  employeeId: number | null;
  action: string;
  description: string | null;
  entityType: string | null;
  entityId: number | null;
  metadata: unknown;
  createdAt: string;
  yard: {
    id: number;
    name: string;
  };
  employee: {
    id: number;
    firstName: string | null;
    lastName: string | null;
    user: {
      id: string;
      name: string;
      email: string;
    } | null;
  } | null;
};

const PAGE_SIZE = 50;

function formatDate(isoString: string): string {
  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(isoString));
}

function getEmployeeLabel(item: HistoryItem): string {
  if (isNil(item.employee)) return "-";
  const { firstName, lastName, user } = item.employee;
  const fullName = [firstName, lastName].filter(Boolean).join(" ");
  return fullName || user?.email || "-";
}

export function HistoryList() {
  const [items, setItems] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);

  const [detailItem, setDetailItem] = useState<HistoryItem | null>(null);

  // Filters
  const [filterYardId, setFilterYardId] = useState<string[]>([]);
  const [filterEmployeeId, setFilterEmployeeId] = useState<string[]>([]);
  const [filterAction, setFilterAction] = useState<string[]>([]);
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");

  // Options derived from loaded data
  const [allItems, setAllItems] = useState<HistoryItem[]>([]);

  const fetchHistory = useCallback(
    async (currentOffset: number) => {
      setLoading(true);

      const query: Record<string, string | number> = {
        limit: PAGE_SIZE,
        offset: currentOffset,
      };

      if (filterYardId.length === 1) {
        query.yardId = Number(filterYardId[0]);
      }
      if (filterEmployeeId.length === 1) {
        query.employeeId = Number(filterEmployeeId[0]);
      }
      if (filterAction.length === 1) {
        query.action = filterAction[0];
      }
      if (filterDateFrom) {
        query.dateFrom = filterDateFrom;
      }
      if (filterDateTo) {
        query.dateTo = filterDateTo;
      }

      const { data, error: apiError } = await apiClient.GET(
        "/api/admin/history",
        {
          params: { query },
        },
      );

      if (apiError) {
        setError("Erreur lors du chargement de l'historique");
        setLoading(false);
        return;
      }

      const transformed = data.data.items.map((item) => ({
        ...item,
        id: String(item.id),
        numericId: item.id,
        employee: item.employee
          ? {
              ...item.employee,
              id: item.employee.id,
            }
          : null,
      }));
      setItems(transformed as HistoryItem[]);
      setTotal(data.data.total);
      setLoading(false);
    },
    [
      filterYardId,
      filterEmployeeId,
      filterAction,
      filterDateFrom,
      filterDateTo,
    ],
  );

  // Load all items once for filter options (no filters, large limit)
  const fetchAllForOptions = useCallback(async () => {
    const { data } = await apiClient.GET("/api/admin/history", {
      params: { query: { limit: 100, offset: 0 } },
    });
    if (data) {
      const transformed = data.data.items.map((item) => ({
        ...item,
        id: String(item.id),
        numericId: item.id,
        employee: item.employee
          ? {
              ...item.employee,
              id: item.employee.id,
            }
          : null,
      }));
      setAllItems(transformed as HistoryItem[]);
    }
  }, []);

  useEffect(() => {
    fetchAllForOptions();
  }, [fetchAllForOptions]);

  useEffect(() => {
    setOffset(0);
    fetchHistory(0);
  }, [fetchHistory]);

  const yardOptions = useMemo(() => {
    const unique = new Map<number, string>();
    for (const item of allItems) {
      unique.set(item.yard.id, item.yard.name);
    }
    return [...unique.entries()]
      .sort(([, a], [, b]) => a.localeCompare(b))
      .map(([id, name]) => ({ label: name, value: String(id) }));
  }, [allItems]);

  const employeeOptions = useMemo(() => {
    const unique = new Map<number, string>();
    for (const item of allItems) {
      if (isNil(item.employee)) continue;
      const label = getEmployeeLabel(item);
      if (label !== "-") {
        unique.set(item.employee.id, label);
      }
    }
    return [...unique.entries()]
      .sort(([, a], [, b]) => a.localeCompare(b))
      .map(([id, label]) => ({ label, value: String(id) }));
  }, [allItems]);

  const actionOptions = useMemo(() => {
    const unique = new Set<string>();
    for (const item of allItems) {
      unique.add(item.action);
    }
    return [...unique]
      .sort()
      .map((action) => ({ label: action, value: action }));
  }, [allItems]);

  const hasActiveFilters =
    filterYardId.length > 0 ||
    filterEmployeeId.length > 0 ||
    filterAction.length > 0 ||
    filterDateFrom !== "" ||
    filterDateTo !== "";

  const clearAllFilters = () => {
    setFilterYardId([]);
    setFilterEmployeeId([]);
    setFilterAction([]);
    setFilterDateFrom("");
    setFilterDateTo("");
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  const handlePrev = () => {
    const newOffset = Math.max(0, offset - PAGE_SIZE);
    setOffset(newOffset);
    fetchHistory(newOffset);
  };

  const handleNext = () => {
    const newOffset = offset + PAGE_SIZE;
    setOffset(newOffset);
    fetchHistory(newOffset);
  };

  const columns: DataTableColumn<HistoryItem>[] = [
    {
      id: "createdAt",
      header: "Date",
      accessor: (row) => formatDate(row.createdAt),
      isSortable: true,
      loadingCell: "text",
    },
    {
      id: "employee",
      header: "Utilisateur",
      accessor: (row) => getEmployeeLabel(row),
      loadingCell: "text",
    },
    {
      id: "yard",
      header: "Chantier",
      accessor: (row) => row.yard.name,
      isSortable: true,
      loadingCell: "text",
    },
    {
      id: "action",
      header: "Action",
      accessor: (row) => row.action,
      loadingCell: "text",
    },
    {
      id: "entityType",
      header: "Entité",
      accessor: (row) => row.entityType ?? "-",
      loadingCell: "text",
    },
    {
      id: "description",
      header: "Description",
      accessor: (row) => row.description ?? "-",
      loadingCell: "text",
    },
    {
      id: "details",
      header: "Détails",
      accessor: () => "",
      cell: ({ row }) =>
        isNil(row.metadata) ? (
          <span className="text-zinc-400">—</span>
        ) : (
          <Button
            plain
            onClick={() => setDetailItem(row)}
            aria-label="Voir les détails"
          >
            <MagnifyingGlassIcon className="size-4" />
          </Button>
        ),
    },
  ];

  if (error) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <>
      <div className="container mx-auto p-6">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
            Historique
          </h2>
          <span className="text-sm text-zinc-500 dark:text-zinc-400">
            {total} entrée{total !== 1 ? "s" : ""}
          </span>
        </div>

        <div className="mb-4 flex items-end gap-3">
          <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <Combobox
              options={yardOptions}
              value={filterYardId}
              onValueChange={({ value }) => setFilterYardId(value)}
              placeholder="Tous les chantiers"
              label="Chantier"
            />
            <Combobox
              options={employeeOptions}
              value={filterEmployeeId}
              onValueChange={({ value }) => setFilterEmployeeId(value)}
              placeholder="Tous les utilisateurs"
              label="Utilisateur"
            />
            <Select
              options={actionOptions}
              value={filterAction}
              onValueChange={({ value }) => setFilterAction(value)}
              placeholder="Toutes les actions"
              label="Action"
            />
            <div>
              <label
                htmlFor="filter-date-from"
                className="mb-1 block text-sm font-medium text-zinc-700 dark:text-zinc-300"
              >
                Date de début
              </label>
              <input
                id="filter-date-from"
                type="date"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
              />
            </div>
            <div>
              <label
                htmlFor="filter-date-to"
                className="mb-1 block text-sm font-medium text-zinc-700 dark:text-zinc-300"
              >
                Date de fin
              </label>
              <input
                id="filter-date-to"
                type="date"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
              />
            </div>
          </div>
          <Button
            plain
            onClick={clearAllFilters}
            disabled={!hasActiveFilters}
            aria-label="Réinitialiser les filtres"
          >
            <XIcon data-slot="icon" />
            Réinitialiser
          </Button>
        </div>

        <DataTable
          columns={columns}
          data={items}
          isLoading={loading}
          isEmpty={items.length === 0}
          emptyState={
            <div className="flex min-h-[200px] items-center justify-center text-gray-500">
              Aucune entrée dans l'historique
            </div>
          }
        />

        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between">
            <Button outline onClick={handlePrev} disabled={currentPage === 1}>
              Précédent
            </Button>
            <span className="text-sm text-zinc-500 dark:text-zinc-400">
              Page {currentPage} sur {totalPages}
            </span>
            <Button
              outline
              onClick={handleNext}
              disabled={currentPage >= totalPages}
            >
              Suivant
            </Button>
          </div>
        )}
      </div>

      <Modal.Root
        open={isNil(detailItem) === false}
        onOpenChange={(details) => {
          if (!details.open) setDetailItem(null);
        }}
      >
        <Modal.Portal>
          <Modal.Backdrop />
          <Modal.Positioner>
            <Modal.Content>
              <Modal.Header>
                <Modal.Title>Détails de l'entrée</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {isNil(detailItem) === false && (
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                        Action
                      </p>
                      <p className="mt-1 font-mono text-sm text-zinc-900 dark:text-zinc-100">
                        {detailItem.action}
                      </p>
                    </div>
                    {isNil(detailItem.description) === false && (
                      <div>
                        <p className="text-xs font-medium uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                          Description
                        </p>
                        <p className="mt-1 text-sm text-zinc-900 dark:text-zinc-100">
                          {detailItem.description}
                        </p>
                      </div>
                    )}
                    {isNil(detailItem.metadata) === false && (
                      <div>
                        <p className="text-xs font-medium uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                          Métadonnées
                        </p>
                        <pre className="mt-1 overflow-auto rounded-md bg-zinc-100 p-3 text-xs text-zinc-800 dark:bg-zinc-800 dark:text-zinc-200">
                          {JSON.stringify(detailItem.metadata, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button onClick={() => setDetailItem(null)}>Fermer</Button>
              </Modal.Footer>
            </Modal.Content>
          </Modal.Positioner>
        </Modal.Portal>
      </Modal.Root>
    </>
  );
}

export type HistoryListProps = Record<string, never>;
